import { useCallback, useEffect, useRef, useState } from 'react';
import { SIM_CMDS, SIM_EVENTS, sanitizeMetrics } from '../lib/simFrame';

const LOAD_TIMEOUT = 10000;

// Listens only to messages from our own iframe window and exposes a whitelisted command sender.
export const useSimBridge = (frameRef, runKey) => {
  const [status, setStatus] = useState('loading');
  const [supports, setSupports] = useState([]);
  const [metrics, setMetrics] = useState([]);
  const [error, setError] = useState('');
  const [lastResult, setLastResult] = useState(null);
  const statusRef = useRef('loading');

  useEffect(() => {
    statusRef.current = 'loading';
    setStatus('loading'); setSupports([]); setMetrics([]); setError(''); setLastResult(null);
    const onMsg = (e) => {
      const win = frameRef.current?.contentWindow;
      if (!win || e.source !== win) return;
      const d = e.data;
      if (!d || d.__amn !== 1 || !SIM_EVENTS.includes(d.type)) return;
      const p = d.payload && typeof d.payload === 'object' ? d.payload : {};
      if (d.type === 'loaded' && statusRef.current === 'loading') { statusRef.current = 'loaded'; setStatus('loaded'); }
      if (d.type === 'ready') {
        statusRef.current = 'ready'; setStatus('ready');
        setSupports((Array.isArray(p.supports) ? p.supports : []).filter(c => SIM_CMDS.includes(c)));
      }
      if (d.type === 'metrics') setMetrics(sanitizeMetrics(d.payload));
      if (d.type === 'result') setLastResult(p);
      if (d.type === 'error') setError(String(p.message || '').slice(0, 160));
    };
    window.addEventListener('message', onMsg);
    const t = setTimeout(() => { if (statusRef.current === 'loading') { statusRef.current = 'timeout'; setStatus('timeout'); } }, LOAD_TIMEOUT);
    return () => { window.removeEventListener('message', onMsg); clearTimeout(t); };
  }, [frameRef, runKey]);

  const send = useCallback((type, payload = {}) => {
    if (!SIM_CMDS.includes(type)) return false;
    const win = frameRef.current?.contentWindow;
    if (!win) return false;
    win.postMessage({ __amn: 1, type, payload }, '*');
    return true;
  }, [frameRef]);

  const can = useCallback((cmd) => status === 'ready' && supports.includes(cmd), [status, supports]);

  return { status, supports, metrics, error, lastResult, send, can };
};
